import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {AbstractField} from "../field/abstract-field";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {OptionComponent} from "./option.component";
import {Option} from "./option";
import {NavigationService} from "../../navigation/navigation.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {FieldComponent} from '../field/field.component';

@Component({
    selector: "woa-selection-field",
    templateUrl: "./selection-field.component.html",
    styleUrl: "./selection-field.component.scss",
    providers: [{provide: NG_VALUE_ACCESSOR, useExisting: SelectionFieldComponent, multi: true}],
    imports: [
        FieldComponent,
        ToggleComponent,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectionFieldComponent<T> extends AbstractField {
    private translate: TranslateService = inject(TranslateService);
    private navigationService: NavigationService = inject(NavigationService);

    public readonly optionsTitle: InputSignal<string> = input<string>();
    public readonly noMessage: InputSignal<boolean> = input<boolean>(false);
    public readonly showMessageSpinner: InputSignal<boolean> = input<boolean>(false);
    public readonly toggleActive: InputSignal<boolean> = input<boolean>(false);
    public readonly showNone: InputSignal<boolean> = input<boolean>(true);

    public onActivateField: OutputEmitterRef<void> = output<void>();

    private optionComponents: Signal<ReadonlyArray<OptionComponent<T>>> = contentChildren(OptionComponent);

    private selectedOption: WritableSignal<Option<T> | undefined> = signal(undefined);

    private options: Signal<Option<T>[] | undefined> = computed(() =>
        this.optionComponents() ? this.optionComponents().map((optionComponent: OptionComponent<T>) => optionComponent.option()) : undefined);

    public optionDisplayValue: Signal<string> = computed(() =>
        this.selectedOption() && this.selectedOption()!.label && this.valueSignal() != undefined
            ? this.translate.instant(this.selectedOption()!.label, {value: this.displayValue()})
            : this.displayValue());

    override writeValue(value: T) {
        super.writeValue(value);
        this.selectedOption.set(this.options() ? this.options().find((option: Option<T>) => option.value === value) : undefined);
    }

    async activated(): Promise<void> {
        if (this.readOnly()) {
            return;
        }

        this.onActivateField.emit();
        if (this.optionComponents().length) {
            const option: Option<T> = await this.navigationService.createPage("woa-options-page", {
                title: this.optionsTitle() || this.label(),
                translationParams: this.translationParams(),
                options: this.options(),
                showNone: this.showNone()
            });
            if (option !== undefined) {
                this.selectedOption.set(option);
                this.setFromInside(option ? option.value : null);
            }
        }
    }
}
